/**
 * §4.11 — the “phantom UPDATE” against the PowerSync view.
 *
 * An `UPDATE … WHERE id = ?` with Drizzle against PowerSync's SQLite view
 * can match ZERO rows and return “success” while the newly written row
 * is still traveling in the local CRUD queue: the view is rebuilt from the
 * checkpoint exactly when that queue empties, and in that specific window the
 * row may not be visible to the UPDATE. The user's write disappears WITHOUT
 * AN ERROR — Drizzle does not throw, `await` resolves normally, and there is no
 * trace that the `.set(...)` did not touch anything.
 *
 * The window is NOT “X ms have passed”: it is “the queue emptied in the
 * middle”. It cannot be detected a priori or by a timer, and a case that lands
 * correctly does not prove the site is protected — only that the window did
 * not happen that time. The only way to know is to write and READ BACK.
 *
 * `patchRowVerified` does: write → read back → compare the fields requested
 * for change → if they do not match, wait for a backoff (giving the CRUD queue
 * time to drain) → retry ONCE → if they still do not match, THROW. It never
 * returns “success” silently.
 *
 * `waitForRowPresent` is the same mechanism for the “in transaction” case: instead
 * of writing and comparing, it only waits for a row to exist before the caller
 * references it (e.g. an FK to a newly synced row). No mutation in this repo
 * needs it today — there is no `upsert` that decides INSERT-vs-UPDATE with a
 * prior SELECT within the same window (the second class of §4.11 defect) — but
 * it is left here, with its own tests, for
 * the day one appears.
 */

const DEFAULT_BACKOFF_MS = 6000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Compares the fields requested in `patch` against the row read back. It only
 * looks at keys present in `patch` (with a value other than `undefined`): these
 * are the fields the mutation requested changing, and they prove that the UPDATE
 * really landed. A missing row (`undefined`) never matches.
 */
function rowMatchesPatch<TRow extends Record<string, unknown>>(
  row: TRow | undefined,
  patch: Partial<TRow>,
): boolean {
  if (!row) {
    return false;
  }
  return Object.entries(patch).every(
    ([key, value]) => value === undefined || row[key as keyof TRow] === value,
  );
}

export interface WriteVerifyOptions {
  /** Wait before the single retry. Defaults to 6 s (canon `listto-app`). */
  backoffMs?: number;
}

/**
 * UPDATE-by-id verified against the PowerSync view.
 *
 * @param applyUpdate Executes `db.update(table).set(patch).where(...)`. It is
 *   called up to twice (write + one retry), so it must be
 *   idempotent — mutations in this repo already are (they set a value, not
 *   increment it).
 * @param reread Reads the SAME row (same `where`) after each
 *   write. It must use the same `db`/view as `applyUpdate` so the
 *   re-read observes the same state that was just written.
 * @param patch The fields — and only those — that the write promises to change.
 *   They are compared against the re-read to decide whether the UPDATE landed.
 * @throws If, after writing, reading back, waiting for the backoff, and retrying once,
 *   the fields still do not match: the write did not land and is NOT accepted
 *   as silent success.
 */
export async function patchRowVerified<TRow extends Record<string, unknown>>(
  applyUpdate: () => Promise<unknown>,
  reread: () => Promise<TRow | undefined>,
  patch: Partial<TRow>,
  opts: WriteVerifyOptions = {},
): Promise<void> {
  const backoffMs = opts.backoffMs ?? DEFAULT_BACKOFF_MS;

  await applyUpdate();
  if (rowMatchesPatch(await reread(), patch)) {
    return;
  }

  // The CRUD queue probably emptied just between the UPDATE and the re-read
  // (or just before the UPDATE): give it time to settle and retry.
  await sleep(backoffMs);

  await applyUpdate();
  if (rowMatchesPatch(await reread(), patch)) {
    return;
  }

  throw new Error(
    "patchRowVerified: the UPDATE was not reflected after re-reading, waiting the backoff and retrying — the row may still be in transit in the PowerSync CRUD queue.",
  );
}

/**
 * Waits for a row to exist in the view before it is referenced — the canon's
 * “in transaction” case: an `upsert` that decides INSERT-vs-UPDATE with a
 * prior SELECT has no `id` to read back until the branch is chosen, so instead
 * of verifying a write, this function verifies a READ before deciding.
 *
 * @throws If the row is still absent after the backoff.
 */
export async function waitForRowPresent<TRow>(
  reread: () => Promise<TRow | undefined>,
  opts: WriteVerifyOptions = {},
): Promise<TRow> {
  const backoffMs = opts.backoffMs ?? DEFAULT_BACKOFF_MS;

  const first = await reread();
  if (first !== undefined) {
    return first;
  }

  await sleep(backoffMs);

  const second = await reread();
  if (second !== undefined) {
    return second;
  }

  throw new Error(
    "waitForRowPresent: the row did not appear in the view after waiting the backoff — it may still be in transit in the PowerSync CRUD queue.",
  );
}
